import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { roleService, RoleCreateType, RoleUpdateType } from "@/services/role.service";
import { notifyError, notifySuccess } from "@/lib/notification";

export const rolesKey = {
  all: ["roles"] as const,
  lists: (filters?: any) => [...rolesKey.all, "lists", filters] as const,
  detail: (id: string) => [...rolesKey.all, "detail", id] as const,
};

export const rolePermissionsKey = {
  all: ["rolePermissions"] as const,
  detail: (roleId: string) => [...rolePermissionsKey.all, roleId] as const,
};

export const useRolePermissionsQuery = (roleId?: string, enabled = true) => {
  return useQuery({
    queryKey: rolePermissionsKey.detail(roleId || "root"),
    queryFn: () => roleService.fetchRolePermissions(roleId),
    enabled: enabled,
  });
};

export const useRoleTemplatesQuery = () => {
  return useQuery({
    queryKey: ["roleTemplates"],
    queryFn: () => roleService.fetchRoleTemplates(),
    staleTime: Infinity, // Templates are static system data
  });
};

export const useUpdateRolePermissionsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, payload }: { roleId: string; payload: { permission_ids: string[] } }) =>
      roleService.updateRolePermissions(roleId, payload),
    onSuccess: (_, variables) => {
      notifySuccess("Permissions updated successfully");
      queryClient.invalidateQueries({ queryKey: rolePermissionsKey.detail(variables.roleId) });
    },
    onError: (error: any) => {
      notifyError(error.response?.data?.message || "Failed to update permissions");
    },
  });
};

export const useCreateRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RoleCreateType) => roleService.createRole(payload),
    onSuccess: () => {
      notifySuccess("Role created successfully");
      queryClient.invalidateQueries({ queryKey: rolesKey.all });
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.message || "Failed to create role"
      );
    },
  });
};

export const useUpdateRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleId, payload }: { roleId: string; payload: RoleUpdateType }) =>
      roleService.updateRole(roleId, payload),
    onSuccess: () => {
      notifySuccess("Role updated successfully");
      queryClient.invalidateQueries({ queryKey: rolesKey.all });
    },
    onError: (error: any) => {
      notifyError(error.response?.data?.error?.message || error.response?.data?.message || "Failed to update role");
    },
  });
};

export const useRolesByDesignationQuery = (designationId?: string) => {
  return useQuery({
    queryKey: rolesKey.lists({ designationId }),
    queryFn: () => roleService.fetchRolesByDesignation(designationId),
  });
};
